import React, { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box, Stack } from '@mui/material';
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router';
import { EventsContext, EventsProvider } from 'context/events';
import { EventsFilterProvider } from 'context/eventsFilter';
import { LeavesContext } from 'context/leaves';
import { Loading } from 'components/common/Loading';
import { EventsScrollLoading } from 'components/events/EventsScrollLoading';
import { GridView } from 'layout/events/GridView';
import { groupEventsByDate } from 'modules/events';
import { EventsFilter } from 'components/events/EventsFilter';
import { EventsModal } from 'components/events/EventsModal';
import { NoEvents } from 'layout/events/NoEvents';
const withEventsContext = (Component: React.ComponentType) => () => <EventsProvider>
    <EventsFilterProvider>
      <EventsFilter />
    </EventsFilterProvider>
    <EventsModal />
    <Component />
  </EventsProvider>;
const EventsComponent = () => {
  const {
    eventId
  } = useSearch({
    from: '/events'
  });
  const {
    eventsStore,
    loading: eventsLoading,
    queryNextPage,
    setModalOpen
  } = useContext(EventsContext);
  const store = eventsStore.read();
  const events = store?.states || [];
  const eventsByDate = groupEventsByDate(events);
  const {
    leafStore,
    loading: leavesLoading
  } = useContext(LeavesContext);
  const leaves = leafStore.read();
  useEffect(() => {
    if (eventId) {
      setModalOpen(true);
    } else {
      setModalOpen(false);
    }
  }, [eventId]);

  // Infinite scroll
  const {
    ref: intersectionObserverTarget
  } = useInView({
    threshold: 1,
    onChange: (inView, _entry) => {
      if (inView) {
        queryNextPage();
      }
    }
  });
  if (leavesLoading || eventsLoading && !events.length) {
    return <Loading />;
  }
  if (eventsLoading || events.length) {
    return <>
        <GridView events={eventsByDate} leaves={leaves} />
        {store.nextPageToken ? <>
            <Box ref={intersectionObserverTarget} />
            <EventsScrollLoading />
          </> : null}
      </>;
  }
  return <Stack alignItems="center" sx={{
    marginTop: '20px',
    marginBottom: '10px'
  }}>
      <NoEvents />
    </Stack>;
};
export const Route = createFileRoute('/events')({
  component: withEventsContext(EventsComponent),
  beforeLoad: async ({
    context
  }) => {
    if (process.env.NODE_ENV === 'production' && !context.appFeatures.events) {
      return redirect({
        to: '/'
      });
    }
  }
});
if (import.meta.hot) {
  import.meta.hot.accept(newModule => {
    if (newModule && newModule.Route && typeof newModule.Route.clone === 'function') {
      newModule.Route.clone(Route);
    }
  });
}